import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SocialCampaigns from "@/components/SocialCampaigns";

interface SocialCampaignsPreviewWrapperProps {
  northId: string;
  address: string;
  showOnReport: boolean;
  viewMode: 'mobile' | 'desktop';
}

export function SocialCampaignsPreviewWrapper({ 
  northId, 
  address, 
  showOnReport, 
  viewMode 
}: SocialCampaignsPreviewWrapperProps) {
  const { data: posts, isLoading } = useQuery({
    queryKey: ["social-campaigns", northId, "v5"],
    staleTime: 0,
    refetchOnMount: true,
    queryFn: async () => {
      const isDemo = northId === 'aaaaaaaa-0000-0000-0000-000000000001';
      const targetNorthId = isDemo ? 'd0daa852-5636-45c6-8a29-0d12ba9cf2d1' : northId;

      const { data: junctionData } = await supabase
        .from("north_social_posts")
        .select("social_post_id")
        .eq("north_id", targetNorthId);

      const { data: directData } = await supabase
        .from("social_media_posts")
        .select("*, account:social_media_accounts(account_name, platform)")
        .eq("north_id", targetNorthId)
        .eq("is_added_to_report", true);

      const seenIds = new Set<string>();
      const allPosts: any[] = [];

      if (junctionData && junctionData.length > 0) {
        const postIds = junctionData.map((item: any) => item.social_post_id);
        const { data: junctionPosts } = await supabase
          .from("social_media_posts")
          .select("*, account:social_media_accounts(account_name, platform)")
          .in("id", postIds)
          .order("posted_at", { ascending: false });
        for (const p of junctionPosts || []) {
          if (!seenIds.has(p.id)) { seenIds.add(p.id); allPosts.push(p); }
        }
      }

      for (const p of directData || []) {
        if (!seenIds.has(p.id)) { seenIds.add(p.id); allPosts.push(p); }
      }

      allPosts.sort((a, b) => new Date(b.posted_at).getTime() - new Date(a.posted_at).getTime());
      return allPosts;
    },
  });

  // Don't render if toggle is off, if loading, or if there's no data
  if (!showOnReport) return null;
  if (isLoading) return null;
  
  const hasData = posts && posts.length > 0;
  if (!hasData) return null;

  return (
    <div className={`rounded-xl bg-gradient-to-br from-secondary/5 via-secondary/10 to-background border border-border/50 shadow-sm ${viewMode === 'mobile' ? 'mb-8 p-6' : 'mb-12 p-8'}`}>
      <SocialCampaigns
        northId={northId}
        address={address}
        forceMobileLayout={viewMode === 'mobile'}
      />
    </div>
  );
}
